/**
 * Minimal GraphQL client for the Ans API (POST /graphql). No extra dependency: it reuses the REST client's base URL,
 * shared API key and Bearer token, and names the active project in the `x-project-id` header, which is how the API
 * learns the project (never from the variables or the body). Errors are thrown as GraphQLRequestError so callers can
 * read the server's `code` (FORBIDDEN, CONFLICT, NOT_FOUND ...).
 */
import { STORAGE_KEYS } from '../constants/storageKeys';
import { ApiError } from '../utils/apiErrors';
import { endSessionIfExpired } from '../utils/session';
import { getApiKey, getApiUrl, getProjectId } from './PlanningApiClient';

export interface GraphQLErrorItem {
  message: string;
  extensions?: { code?: string; issues?: Array<{ path?: Array<string | number>; message: string }> } & Record<string, unknown>;
}

export class GraphQLRequestError extends Error {
  readonly errors: GraphQLErrorItem[];
  readonly status: number;
  constructor(errors: GraphQLErrorItem[], status = 200) {
    super(errors[0]?.message ?? 'The request failed.');
    this.name = 'GraphQLRequestError';
    this.errors = errors;
    this.status = status;
  }
  get code(): string | undefined {
    return this.errors[0]?.extensions?.code;
  }
}

export function isGraphQLRequestError(e: unknown): e is GraphQLRequestError {
  return e instanceof GraphQLRequestError;
}

export async function graphqlRequest<T>(query: string, variables?: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', 'x-api-key': getApiKey() };
  const project = getProjectId();
  if (project) headers['x-project-id'] = project;
  const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}/graphql`, { method: 'POST', headers, body: JSON.stringify({ query, variables }), signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw ApiError.offline();
  }
  const body = await res.json().catch(() => null) as { data?: T; errors?: GraphQLErrorItem[] } | null;
  if (res.status === 401) endSessionIfExpired(res.status, token);
  if (body?.errors?.length) throw new GraphQLRequestError(body.errors, res.status);
  if (!res.ok || !body || body.data === undefined) throw new GraphQLRequestError([{ message: `The server answered ${res.status}.` }], res.status);
  return body.data;
}
